import { useState } from "react";
import { Plus, Loader2, Trash2, Star, StarOff, Mail, Phone, Send, KeyRound, UserX, Pencil } from "lucide-react";
import { Badge, Button, Input } from "@wa/ui";
import { type PortalModuleGrant } from "@wa/core/portal-modules";
import { useT, useI18n } from "../../../i18n/I18nProvider";
import {
  type Client, type Contact,
  addContact, updateContact, deleteContact,
  inviteContactPortal, unlinkContactPortal,
} from "../../../lib/clients";
import { ApiError } from "../../../lib/api";
import { PortalInviteLapsed, PortalLastSeen } from "../_shared/PortalBadge";
import { PhoneLink } from "../../telephony/PhoneLink";
import { ContactPortalModules } from "./ContactPortalModules";
import { ContactEditModal } from "./ContactEditModal";
import { ContactPortalRevokeModal, type PortalRevokeTarget } from "./ContactPortalRevokeModal";

type T = ReturnType<typeof useT>;

/**
 * The client's people, and their way into the client portal.
 *
 * Moved out of `ClientsSection.tsx` when per-module portal grants arrived: the
 * tab was the largest thing in a file that also held the client list, the
 * detail shell and three other tabs, and it is the only one of them that is
 * about a PERSON rather than about the company. The move is a move; the portal
 * grant control below is the new part.
 */

export function ContactsTab({ t, client, canManage, companyGrant, onChange }: {
  t: T; client: Client & { contacts: Contact[] }; canManage: boolean;
  /**
   * What this company may open, CRM-wide default already folded in, passed
   * straight through to every contact's grant control.
   *
   * Resolved by the parent rather than here, and required rather than optional,
   * for the same reason `ModuleGrantLevels` makes every level mandatory: an
   * omitted level defaults to `null`, `null` reads as unrestricted, and a
   * missing prop would therefore widen what the row claims about a restricted
   * company with nothing anywhere to notice it. Required makes that omission a
   * build failure instead.
   */
  companyGrant: PortalModuleGrant;
  onChange: () => void;
}) {
  const { locale } = useI18n();
  const [showForm, setShowForm] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [position, setPosition] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!firstName.trim()) return;
    setBusy(true);
    try {
      await addContact(client.id, {
        firstName: firstName.trim(), lastName: lastName || undefined,
        email: email || undefined, phone: phone || undefined, position: position || undefined,
        isPrimary: client.contacts.length === 0,
      });
      setFirstName(""); setLastName(""); setEmail(""); setPhone(""); setPosition("");
      setShowForm(false);
      onChange();
    } finally { setBusy(false); }
  }

  const [linkBusy, setLinkBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** The contact whose details are open for editing, by id rather than by row:
   *  `onChange` reloads the client, and a held object would go stale. */
  const [editingId, setEditingId] = useState<string | null>(null);
  /** The contact whose portal access is about to be taken away, if any. */
  const [revoking, setRevoking] = useState<PortalRevokeTarget | null>(null);

  const editing = client.contacts.find((c) => c.id === editingId) ?? null;

  async function togglePrimary(c: Contact) {
    await updateContact(client.id, c.id, { isPrimary: !c.isPrimary });
    onChange();
  }
  async function remove(c: Contact) {
    await deleteContact(client.id, c.id);
    onChange();
  }
  async function grantPortal(c: Contact) {
    setNotice(null);
    setLinkBusy(c.id);
    try {
      // A User with that e-mail is linked straight away; otherwise an invite is sent.
      const res = await inviteContactPortal(client.id, c.id, locale);
      if ("invited" in res) {
        // The invite row exists in every one of these branches; only delivery
        // differs, so the wording is what has to change, not the flow.
        if (res.heldBy) setNotice(t("clients.portalInviteHeld", { email: res.email, inbox: res.heldBy }));
        else if (res.sendFailed) setNotice(t("clients.portalInviteSendFailed", { email: res.email }));
        else setNotice(t("clients.portalInviteSent", { email: res.email }));
      }
      onChange();
    } catch (err) {
      setNotice(err instanceof ApiError && err.status === 409 ? t("clients.portalLinked") : t("clients.portalLinkFailed"));
    } finally {
      setLinkBusy(null);
    }
  }
  /**
   * Taking access away closes BOTH doors, so it asks first.
   *
   * The account link can be handed back with the button next to it; an
   * unredeemed invitation cannot, because destroying the row is what makes the
   * link in the client's mail stop working and there is no copy of the token to
   * put back.
   */
  async function unlinkPortal(contactId: string) {
    setNotice(null);
    setLinkBusy(contactId);
    try {
      const res = await unlinkContactPortal(client.id, contactId);
      // Say what was actually closed. "Access removed" on a contact who had none
      // is the message that teaches an operator to distrust the next one.
      if (res.revoked?.invites) setNotice(t("clients.portalRevoke.doneInvite", { n: res.revoked.invites }));
      else if (res.revoked?.link) setNotice(t("clients.portalRevoke.doneLink"));
      else setNotice(t("clients.portalRevoke.doneNothing"));
      onChange();
    } finally {
      setLinkBusy(null);
      setRevoking(null);
    }
  }

  return (
    <div className="space-y-3">
      {notice && (
        <p className="rounded-md border border-amber-300/50 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
          {notice}
        </p>
      )}
      {client.contacts.length === 0 && !showForm && (
        <p className="text-sm text-muted-foreground">{t("clients.contactsEmpty")}</p>
      )}
      <ul className="space-y-2">
        {client.contacts.map((c) => (
          <li key={c.id} className="flex items-center justify-between gap-3 rounded-lg border border-border p-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{c.firstName} {c.lastName}</span>
                {c.isPrimary && <Badge className="gap-1 text-[10px]"><Star className="size-2.5" /> {t("clients.primary")}</Badge>}
                {c.portalUser && (
                  <Badge variant="success" className="gap-1 text-[10px]" title={c.portalUser.email}>
                    <KeyRound className="size-2.5" /> {t("clients.portalLinked")}
                  </Badge>
                )}
                {!c.portalUser && c.portalInvitePending && (
                  <Badge variant="outline" className="gap-1 text-[10px] text-amber-700 dark:text-amber-300" title={t("clients.portalInvitePendingHint")}>
                    <Send className="size-2.5" /> {t("clients.portalInvitePending")}
                  </Badge>
                )}
              </div>
              <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                {c.position && <span>{c.position}</span>}
                {c.email && <span className="inline-flex items-center gap-1"><Mail className="size-3" />{c.email}</span>}
                {/* Only for a contact who HAS access. On anyone else the answer
                    would be "never", which is true of everybody who was never
                    given a door and says nothing about them. */}
                {c.portalUser && <PortalLastSeen lastSeenAt={c.portalUser.lastSeenAt} />}
                {!c.portalUser && !c.portalInvitePending && <PortalInviteLapsed at={c.portalInviteLapsedAt} />}
                {c.phone && (
                  <span className="inline-flex items-center gap-1">
                    <Phone className="size-3" />
                    {/* The contact's own name, not the client's: the call pane
                        prints `companyName` as who was on the line, and the
                        operator dialled a person here.

                        `contactId` instead of `clientId`, and that is the point
                        of this button now: the name above was a STRING copied
                        onto the row, so the person could be read and never
                        followed. The id makes the call show up on this person's
                        own history, and the server fills the company in from
                        the contact itself. */}
                    <PhoneLink
                      phone={c.phone}
                      source="manual"
                      companyName={`${c.firstName} ${c.lastName ?? ""}`.trim()}
                      contactId={c.id}
                      hideIcon
                      className="text-xs"
                    />
                  </span>
                )}
              </div>
              {/* Only where portal access could ever exist. A contact with no
                  mail address can never be linked or invited, so a grant
                  control there would be a setting with no door behind it. */}
              {c.email && (
                <ContactPortalModules
                  clientId={client.id}
                  contact={c}
                  companyGrant={companyGrant}
                  canManage={canManage}
                  onChange={onChange}
                />
              )}
            </div>
            {canManage && (
              <div className="flex shrink-0 items-center gap-1">
                {/* Revoke is offered for an outstanding INVITATION too, not only
                    for a linked account. Without it a misaddressed invitation
                    had no undo at all: the only control on the row was "send it
                    again", and the link stayed good for the rest of its week. */}
                {(c.portalUser || c.portalInvitePending) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setRevoking({
                      id: c.id,
                      name: `${c.firstName} ${c.lastName ?? ""}`.trim(),
                      email: c.portalUser?.email ?? c.email,
                      linked: !!c.portalUser,
                      invited: !!c.portalInvitePending,
                    })}
                    disabled={linkBusy === c.id}
                    title={t(c.portalUser ? "clients.portalUnlink" : "clients.portalRevokeInvite")}
                  >
                    {linkBusy === c.id ? <Loader2 className="size-3.5 animate-spin" /> : <UserX className="size-3.5" />}
                  </Button>
                )}
                {!c.portalUser && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => grantPortal(c)}
                    disabled={linkBusy === c.id || !c.email}
                    title={c.email ? t(c.portalInvitePending ? "clients.portalInviteAgain" : "clients.portalGrant") : t("clients.portalNeedsEmail")}
                  >
                    {linkBusy === c.id ? <Loader2 className="size-3.5 animate-spin" /> : c.portalInvitePending ? <Send className="size-3.5" /> : <KeyRound className="size-3.5" />}
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => setEditingId(c.id)} title={t("clients.contactEditTitle")}>
                  <Pencil className="size-3.5" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => togglePrimary(c)} title={t("clients.makePrimary")}>
                  {c.isPrimary ? <StarOff className="size-3.5" /> : <Star className="size-3.5" />}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => remove(c)} className="text-red-600">
                  <Trash2 className="size-3.5" />
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>

      {canManage && (showForm ? (
        <form onSubmit={submit} className="space-y-2 rounded-lg border border-border p-3">
          <div className="grid grid-cols-2 gap-2">
            <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder={t("clients.firstName")} required />
            <Input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder={t("clients.lastName")} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("clients.email")} />
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t("clients.phone")} />
          </div>
          <Input value={position} onChange={(e) => setPosition(e.target.value)} placeholder={t("clients.position")} />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setShowForm(false)}>{t("clients.cancel")}</Button>
            <Button type="submit" size="sm" disabled={busy || !firstName.trim()}>
              {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Plus className="size-3.5" />}
              {t("clients.addContact")}
            </Button>
          </div>
        </form>
      ) : (
        <Button variant="outline" size="sm" onClick={() => setShowForm(true)}>
          <Plus className="size-3.5" /> {t("clients.addContact")}
        </Button>
      ))}

      {/* Looked up from the freshly loaded list, so a save that reloads the
          client cannot leave the form holding the previous row. */}
      {editing && (
        <ContactEditModal
          clientId={client.id}
          contact={editing}
          onClose={() => setEditingId(null)}
          onSaved={onChange}
        />
      )}

      {revoking && (
        <ContactPortalRevokeModal
          target={revoking}
          busy={linkBusy === revoking.id}
          onConfirm={() => void unlinkPortal(revoking.id)}
          onClose={() => setRevoking(null)}
        />
      )}
    </div>
  );
}
